import type { FC } from 'react'
import { CheckCircleIcon, XCircleIcon, ArrowPathIcon } from '@heroicons/react/24/solid'

export type SaveStatus = 'idle' | 'saving' | 'error'

interface AutoSaveStatusProps {
  status: SaveStatus
  onRetry?: () => void
}

/**
 * Auto-save status indicator that persists across tab switches
 * Shows idle (saved), saving (in progress), or error (with retry) states
 */
export const AutoSaveStatus: FC<AutoSaveStatusProps> = ({ status, onRetry }) => {
  if (status === 'idle') {
    return (
      <div
        className="flex items-center gap-2 text-green-600"
        data-testid="auto-save-status"
        data-status="idle"
        role="status"
        aria-live="polite"
      >
        <CheckCircleIcon className="w-5 h-5" aria-hidden="true" />
        <span className="text-sm font-medium">All changes saved</span>
      </div>
    )
  }

  if (status === 'saving') {
    return (
      <div
        className="flex items-center gap-2 text-gray-600"
        data-testid="auto-save-status"
        data-status="saving"
        role="status"
        aria-live="polite"
      >
        <ArrowPathIcon className="w-5 h-5 animate-spin" aria-hidden="true" />
        <span className="text-sm font-medium">Saving changes...</span>
      </div>
    )
  }

  // Error state
  return (
    <div
      className="space-y-2"
      data-testid="auto-save-status"
      data-status="error"
      role="alert"
      aria-live="assertive"
    >
      <div className="flex items-center gap-2 text-red-600">
        <XCircleIcon className="w-5 h-5" aria-hidden="true" />
        <span className="text-sm font-medium">Failed to save</span>
      </div>
      {onRetry && (
        <button
          onClick={onRetry}
          className="w-full px-3 py-1.5 bg-red-100 text-red-700 rounded text-sm font-medium hover:bg-red-200 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
          data-testid="auto-save-retry"
        >
          Retry
        </button>
      )}
    </div>
  )
}

export default AutoSaveStatus
